/** Throwaway base for resolving a candidate path the way a browser would. */
const PROBE_ORIGIN = 'https://redirect-probe.invalid';

/**
 * Validate redirect path to prevent open redirects — allows only relative paths
 * starting with a single slash. Rejects protocol-relative URLs (`//evil.com`),
 * backslash-prefixed paths (`/\evil.com`), and anything with a scheme
 * (`https://…`, `javascript:…`) because those never start with `/`.
 *
 * FRE-1389: the prefix checks alone are not enough. The WHATWG URL parser
 * deletes tab / LF / CR anywhere in the input and treats `\` as `/`, so
 * `/\t/evil.com` passes a character check yet navigates to `//evil.com`. The
 * candidate is therefore also resolved against a probe origin and must stay
 * on it — the check runs on what the browser will actually navigate to.
 *
 * @param redirectPath candidate path, typically from a query param.
 * @param fallback path returned when validation fails. Defaults to `/` so the
 *   existing callers see no behavior change; callers that have a more specific
 *   safe landing page (e.g. `/login?force=1` for the stale-cookie escape
 *   hatch) can override.
 */
export function getValidRedirect(redirectPath: string | null, fallback: string = '/'): string {
  if (!redirectPath) return fallback;
  if (!redirectPath.startsWith('/') || redirectPath[1] === '/' || redirectPath[1] === '\\') {
    return fallback;
  }
  try {
    if (new URL(redirectPath, PROBE_ORIGIN).origin !== PROBE_ORIGIN) return fallback;
  } catch {
    return fallback;
  }
  return redirectPath;
}
